import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { JOURNEY_STAGE_LABELS, type JourneyStage } from "@shared/schema";
import { isHiddenAppPath } from "@shared/hidden-sections";
import { Button } from "@/components/ui/button";

type MeResponse = { user?: { journeyStage?: JourneyStage | null } } | undefined;

export function useJourneyStage(): JourneyStage | null {
  const { data } = useQuery<MeResponse>({ queryKey: ["/api/app/auth/me"] });
  const stage = data?.user?.journeyStage;
  return stage ?? null;
}

export type JourneyEmptyAction = {
  label: string;
  href?: string;
  onClick?: () => void;
  variant?: "default" | "outline";
};

export type JourneyEmptyContent = {
  title: string;
  description: string;
  actions?: JourneyEmptyAction[];
};

export type JourneyEmptyPage =
  | "properties"
  | "acquisitions"
  | "renovations"
  | "maintenance"
  | "members"
  | "financials"
  | "devices"
  | "devices_locks"
  | "devices_thermostats"
  | "messages"
  | "supplies"
  | "supplies_catalog"
  | "supplies_recurring"
  | "vendors";

const FALLBACK: Record<JourneyEmptyPage, JourneyEmptyContent> = {
  properties: {
    title: "No properties yet",
    description: "Add your first CoLiving home to start tracking rooms, members, and operations.",
    actions: [],
  },
  acquisitions: {
    title: "No acquisitions found",
    description: "Add your first acquisition to start tracking offers, contracts, and closings.",
    actions: [{ label: "New Acquisition", variant: "default" }],
  },
  renovations: {
    title: "No properties in renovation phase",
    description: "Set a property's status to \"Renovations\" from the Properties page to begin tracking.",
    actions: [{ label: "Open Properties", href: "/app/properties", variant: "outline" }],
  },
  maintenance: {
    title: "No maintenance tasks yet",
    description: "Add a task or wait for member-reported issues to land here.",
  },
  members: {
    title: "No members yet",
    description: "Add your first member to start tracking residents across your homes.",
    actions: [{ label: "Add Member", variant: "default" }],
  },
  financials: {
    title: "No expenses recorded yet",
    description: "Click \"Add Expense\" to start tracking your operating costs.",
    actions: [{ label: "Add Expense", variant: "default" }],
  },
  devices: {
    title: "No devices connected yet",
    description: "Connect smart locks and thermostats so you can manage access and climate from one place.",
    actions: [{ label: "Open Smart Locks", href: "/app/devices/smart-locks", variant: "outline" }],
  },
  devices_locks: {
    title: "No smart locks connected yet",
    description: "Connect a smart-lock account to manage access codes from one place.",
    actions: [{ label: "Open Smart Locks", href: "/app/devices/smart-locks", variant: "outline" }],
  },
  devices_thermostats: {
    title: "No thermostats connected yet",
    description: "Connect a thermostat account to monitor and control climate from one place.",
    actions: [{ label: "Open Thermostats", href: "/app/devices/thermostats", variant: "outline" }],
  },
  messages: {
    title: "No conversations found",
    description: "Inbound texts from members and prospects will appear here as they come in.",
  },
  supplies: {
    title: "No supply orders found",
    description: "Create your first order or wait for member supply requests to come in.",
    actions: [{ label: "Create First Order", variant: "default" }],
  },
  supplies_catalog: {
    title: "No supply items in catalog",
    description: "Add the items you reorder most so requests are one click away.",
    actions: [{ label: "Add First Item", variant: "default" }],
  },
  supplies_recurring: {
    title: "No recurring schedules",
    description: "Set up auto-reminders for items you reorder on a regular cadence.",
    actions: [{ label: "Add First Schedule", variant: "default" }],
  },
  vendors: {
    title: "No vendors yet",
    description: "Add cleaners, handymen, and other vendors in the Vendor CRM to assign them to your homes.",
    actions: [{ label: "Open Vendor CRM", href: "/app/operations/vendors", variant: "outline" }],
  },
};

const COPY: Record<JourneyEmptyPage, Record<JourneyStage, JourneyEmptyContent>> = {
  properties: {
    searching: {
      title: "Start with research, not data entry",
      description: "You don't have a CoLiving home yet — sharpen your buy box and review market research first. Properties will fill in once you start underwriting deals.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
        { label: "Configure Buy Box", href: "/app/buy-box/settings", variant: "outline" },
      ],
    },
    under_contract: {
      title: "Add the home you're closing on",
      description: "Add the property under contract so you can track inspections, appraisal, and the path to closing alongside your acquisition.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    renovating: {
      title: "Add the home you're renovating",
      description: "Add the property you're working on so you can plan scope, budget, and a projected launch date in Renovations.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "Add your operating CoLiving home",
      description: "Add the home you're running so you can manage rooms, members, maintenance, and revenue in one place.",
      actions: [],
    },
    operating_with_pm: {
      title: "Add the home your PM runs",
      description: "Bring your operating property in so you can monitor performance and approvals alongside your property manager.",
      actions: [],
    },
  },
  acquisitions: {
    searching: {
      title: "No deals to track yet — start with research",
      description: "Once you find a candidate property, underwrite it and convert it into an acquisition. Until then, focus on dialing in your buy box.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
        { label: "Configure Buy Box", href: "/app/buy-box/settings", variant: "outline" },
      ],
    },
    under_contract: {
      title: "Add your contract",
      description: "You said you have a home under contract — add it here to track inspection, appraisal, financing, and closing dates in one place.",
      actions: [{ label: "New Acquisition", variant: "default" }],
    },
    renovating: {
      title: "No active deals right now",
      description: "When you line up your next CoLiving deal, track it here. For now, focus on wrapping up the renovation that's underway.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
        { label: "New Acquisition", variant: "default" },
      ],
    },
    operating_self_managed: {
      title: "No deals in the pipeline",
      description: "When you're ready to add your next CoLiving home, start the deal here to track it from offer through closing.",
      actions: [{ label: "New Acquisition", variant: "default" }],
    },
    operating_with_pm: {
      title: "No deals in the pipeline",
      description: "When your next CoLiving deal comes together, track it here from offer through closing.",
      actions: [{ label: "New Acquisition", variant: "default" }],
    },
  },
  renovations: {
    searching: {
      title: "Renovations come after closing",
      description: "Since you're still searching, there's nothing to renovate yet. Lock down a deal first and we'll help you plan the scope here.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    under_contract: {
      title: "Plan ahead while you close",
      description: "Once you close, set the property's status to \"Renovations\" on the Properties page and we'll help you build the scope, schedule, and budget.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "default" },
        { label: "Open Properties", href: "/app/properties", variant: "outline" },
      ],
    },
    renovating: {
      title: "Set your home to \"Renovations\" to start",
      description: "Open the property you're working on and switch its status to \"Renovations\" — your scope, budget, and projected launch date will live here.",
      actions: [
        { label: "Open Properties", href: "/app/properties", variant: "default" },
      ],
    },
    operating_self_managed: {
      title: "No active renovation projects",
      description: "When you take on a value-add or refresh, set that home's status to \"Renovations\" on the Properties page to plan and track it here.",
      actions: [
        { label: "Open Properties", href: "/app/properties", variant: "outline" },
      ],
    },
    operating_with_pm: {
      title: "No active renovation projects",
      description: "When you take on a renovation, set that home's status to \"Renovations\" on the Properties page so you and your PM can track it here.",
      actions: [
        { label: "Open Properties", href: "/app/properties", variant: "outline" },
      ],
    },
  },
  maintenance: {
    searching: {
      title: "Maintenance comes once you have members",
      description: "You don't have a home in operation yet, so there are no tasks to triage. When members start reporting issues, you'll triage them here by priority and SLA.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
      ],
    },
    under_contract: {
      title: "Get ready for member requests",
      description: "Once your home opens, member-reported issues will land here. Use this time to set SLA targets and decide who handles each category.",
      actions: [
        { label: "Maintenance Settings", href: "/app/tasks/settings", variant: "outline" },
      ],
    },
    renovating: {
      title: "Punch list lives in Renovations for now",
      description: "While you're renovating, track scope and punch-list items in Renovations. Member maintenance tickets will start landing here once the home opens.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "default" },
        { label: "Maintenance Settings", href: "/app/tasks/settings", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No maintenance tasks yet",
      description: "Add a task to get ahead of recurring work, or wait for member-reported issues to come in — they'll appear here automatically.",
    },
    operating_with_pm: {
      title: "No maintenance tasks yet",
      description: "Tasks reported by your members or routed by your property manager will show up here as they come in.",
    },
  },
  members: {
    searching: {
      title: "Members come after you open the doors",
      description: "You don't have a CoLiving home in operation yet — when residents move in, they'll show up here. For now, focus on finding your first deal.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    under_contract: {
      title: "Members will land here after launch",
      description: "Once you close and open the home, residents will appear here automatically. Use this time to wrap up your acquisition checklist.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    renovating: {
      title: "Members will arrive once the doors open",
      description: "While the home is being renovated, focus on getting it launch-ready. Residents will start populating this list once they move in.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No members yet",
      description: "Add your first member or wait for them to sync over from PadSplit — they'll all live here.",
      actions: [{ label: "Add Member", variant: "default" }],
    },
    operating_with_pm: {
      title: "No members yet",
      description: "Members managed by your property manager will appear here once they're synced.",
    },
  },
  financials: {
    searching: {
      title: "Financials kick in after you close",
      description: "There aren't any operating costs to log yet. Sharpen your buy box and underwriting so the deal you choose actually pencils.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
        { label: "Configure Buy Box", href: "/app/buy-box/settings", variant: "outline" },
      ],
    },
    under_contract: {
      title: "Start logging closing-related costs",
      description: "Track inspection, appraisal, and other deal expenses here so your basis is dialed in by the time you close.",
      actions: [
        { label: "Add Expense", variant: "default" },
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    renovating: {
      title: "Track your renovation spend here",
      description: "Log materials, labor, and permits as they come in so you can see actual vs. budget alongside your renovation plan.",
      actions: [
        { label: "Add Expense", variant: "default" },
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No expenses recorded yet",
      description: "Click \"Add Expense\" to start tracking your operating costs and watch your home's profitability in real time.",
      actions: [{ label: "Add Expense", variant: "default" }],
    },
    operating_with_pm: {
      title: "No expenses recorded yet",
      description: "Bring in the operating costs your property manager shares so you have the full P&L in one place.",
      actions: [{ label: "Add Expense", variant: "default" }],
    },
  },
  devices: {
    searching: {
      title: "Devices come once you have a home",
      description: "Smart locks and thermostats get connected after you take possession. Find your first deal and you'll set them up here.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
      ],
    },
    under_contract: {
      title: "Get ready to connect devices at closing",
      description: "Once you take possession, connect smart locks and thermostats here so move-in day is seamless for your first members.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    renovating: {
      title: "Plan your device install during renovation",
      description: "Pick smart locks and thermostats now so they're ready to go online the day you launch.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No devices connected yet",
      description: "Connect smart locks and thermostats so you can manage access codes and climate from one place.",
      actions: [
        { label: "Open Smart Locks", href: "/app/devices/smart-locks", variant: "default" },
        { label: "Open Thermostats", href: "/app/devices/thermostats", variant: "outline" },
      ],
    },
    operating_with_pm: {
      title: "No devices connected yet",
      description: "Connect your devices so you and your property manager can monitor access and climate together.",
      actions: [
        { label: "Open Smart Locks", href: "/app/devices/smart-locks", variant: "outline" },
      ],
    },
  },
  devices_locks: {
    searching: {
      title: "Smart locks come once you have a home",
      description: "Smart locks get installed after you take possession. Find your first deal and you'll connect them here.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
      ],
    },
    under_contract: {
      title: "Get ready to install smart locks at closing",
      description: "Once you take possession, connect a smart-lock account here so move-in day codes are ready to go.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    renovating: {
      title: "Plan your smart-lock install during renovation",
      description: "Pick smart locks now so they're online and assigned to rooms the day you launch.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No smart locks connected yet",
      description: "Connect a smart-lock account to manage access codes for members and vendors from one place.",
      actions: [
        { label: "Open Smart Locks", href: "/app/devices/smart-locks", variant: "default" },
      ],
    },
    operating_with_pm: {
      title: "No smart locks connected yet",
      description: "Connect your smart locks so you and your property manager can issue and revoke codes together.",
      actions: [
        { label: "Open Smart Locks", href: "/app/devices/smart-locks", variant: "outline" },
      ],
    },
  },
  devices_thermostats: {
    searching: {
      title: "Thermostats come once you have a home",
      description: "Smart thermostats get connected after you take possession. Find your first deal and you'll set them up here.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
      ],
    },
    under_contract: {
      title: "Get ready to connect thermostats at closing",
      description: "Once you take possession, connect a thermostat account here so climate is dialed in for move-in.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    renovating: {
      title: "Plan your thermostat install during renovation",
      description: "Pick smart thermostats now so they're online and on schedules the day you launch.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No thermostats connected yet",
      description: "Connect a thermostat account to monitor and control climate across your homes.",
      actions: [
        { label: "Open Thermostats", href: "/app/devices/thermostats", variant: "default" },
      ],
    },
    operating_with_pm: {
      title: "No thermostats connected yet",
      description: "Connect your thermostats so you and your property manager can manage climate together.",
      actions: [
        { label: "Open Thermostats", href: "/app/devices/thermostats", variant: "outline" },
      ],
    },
  },
  messages: {
    searching: {
      title: "Conversations start after move-ins",
      description: "Once members are in your home, every text and SMS reply lands here. For now, focus on closing your first deal.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
      ],
    },
    under_contract: {
      title: "Messages come after move-ins",
      description: "Once your home opens and members move in, every conversation will land in this inbox.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    renovating: {
      title: "Messages come after move-ins",
      description: "When the home opens, member texts and SMS replies will all flow into this inbox automatically.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No conversations yet",
      description: "Inbound texts from members and prospects will show up here as they come in. You can also start a conversation manually.",
    },
    operating_with_pm: {
      title: "No conversations yet",
      description: "Member messages routed your way will land here as they come in.",
    },
  },
  supplies_catalog: {
    searching: {
      title: "Build your catalog later — research first",
      description: "There's nothing to stock yet. Lock in a deal first and you'll build your reorder catalog here so launch day is one click away.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
        { label: "Configure Buy Box", href: "/app/buy-box/settings", variant: "outline" },
      ],
    },
    under_contract: {
      title: "Plan your starter catalog",
      description: "While you close, sketch out the supplies you'll reorder regularly — paper goods, cleaning, light bulbs — so the first order is fast.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    renovating: {
      title: "Stock the catalog before launch",
      description: "Use renovation downtime to add the items you'll reorder most so opening day supply runs are one click each.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No supply items in catalog",
      description: "Add the items you reorder regularly so requests and recurring schedules are quick to set up.",
      actions: [{ label: "Add First Item", variant: "default" }],
    },
    operating_with_pm: {
      title: "No supply items in catalog",
      description: "Capture the items you and your property manager reorder most so requests stay consistent across homes.",
      actions: [{ label: "Add First Item", variant: "default" }],
    },
  },
  supplies_recurring: {
    searching: {
      title: "Recurring orders come after launch",
      description: "There's nothing to schedule yet. Once you have a home in operation, set up auto-reminders for items you reorder on a regular cadence.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
      ],
    },
    under_contract: {
      title: "Plan reorders for opening day",
      description: "Once you close and your catalog is built, set up recurring reminders here for paper goods, cleaning, and other regular reorders.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    renovating: {
      title: "Set up reorder cadences before launch",
      description: "While renovations wrap up, build your catalog and queue up recurring schedules so reorders run themselves the day you open.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No recurring schedules yet",
      description: "Pick an item from your catalog and set a cadence — we'll remind you when it's time to reorder.",
      actions: [{ label: "Add First Schedule", variant: "default" }],
    },
    operating_with_pm: {
      title: "No recurring schedules yet",
      description: "Set cadences for items you and your property manager reorder regularly so nothing runs out between visits.",
      actions: [{ label: "Add First Schedule", variant: "default" }],
    },
  },
  supplies: {
    searching: {
      title: "Supplies kick in after the home opens",
      description: "There's nothing to order yet. Once you have a home in operation, supply runs and reorders will live here.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
      ],
    },
    under_contract: {
      title: "Plan supplies for opening day",
      description: "Once you close, build out your starter catalog so the first order is one click away when members move in.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    renovating: {
      title: "Stock up before launch",
      description: "Use this time to build your supply catalog so reorders are simple the day members move in.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No supply orders yet",
      description: "Create your first order or wait for member supply requests — they'll all flow through this list.",
      actions: [{ label: "Create First Order", variant: "default" }],
    },
    operating_with_pm: {
      title: "No supply orders yet",
      description: "Track supply runs you or your property manager coordinate here so nothing slips through the cracks.",
      actions: [{ label: "Create First Order", variant: "default" }],
    },
  },
  vendors: {
    searching: {
      title: "Vendors come once you have a home in operation",
      description: "Cleaners, handymen, and other vendors get set up after you take possession. For now, focus on finding your first deal.",
      actions: [
        { label: "Browse Research", href: "/app/research", variant: "default" },
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "outline" },
      ],
    },
    under_contract: {
      title: "Line up vendors before opening day",
      description: "Once you close, add the cleaners, handymen, and contractors you'll rely on so they're ready to assign when members move in.",
      actions: [
        { label: "Open Acquisitions", href: "/app/acquisitions", variant: "default" },
        { label: "Open Vendor CRM", href: "/app/operations/vendors", variant: "outline" },
      ],
    },
    renovating: {
      title: "Build your vendor bench during renovation",
      description: "Use this time to add the cleaners and handymen you'll need at launch. Renovation contractors live in Renovations for now.",
      actions: [
        { label: "Open Renovations", href: "/app/renovations", variant: "default" },
        { label: "Open Vendor CRM", href: "/app/operations/vendors", variant: "outline" },
      ],
    },
    operating_self_managed: {
      title: "No vendors yet",
      description: "Add cleaners, handymen, and other vendors in the Vendor CRM so you can assign them to homes and grant lock access here.",
      actions: [
        { label: "Open Vendor CRM", href: "/app/operations/vendors", variant: "default" },
      ],
    },
    operating_with_pm: {
      title: "No vendors yet",
      description: "Add the vendors you and your property manager rely on in the Vendor CRM so they're easy to assign to homes.",
      actions: [
        { label: "Open Vendor CRM", href: "/app/operations/vendors", variant: "outline" },
      ],
    },
  },
};

export function getJourneyEmptyContent(
  page: JourneyEmptyPage,
  stage: JourneyStage | null,
): JourneyEmptyContent {
  if (stage && COPY[page][stage]) return COPY[page][stage];
  return FALLBACK[page];
}

export function JourneyEmptyState({
  page,
  stage,
  icon,
  primaryAction,
  testIdPrefix,
}: {
  page: JourneyEmptyPage;
  stage: JourneyStage | null;
  icon?: React.ReactNode;
  primaryAction?: { label?: string; onClick: () => void };
  testIdPrefix?: string;
}) {
  const content = getJourneyEmptyContent(page, stage);
  const prefix = testIdPrefix || `empty-${page}`;
  return (
    <div className="text-center py-12 px-6 border rounded-lg bg-white" data-testid={prefix}>
      {icon && <div className="mx-auto mb-3 flex justify-center opacity-60">{icon}</div>}
      <p className="text-base font-semibold text-gray-900" data-testid={`${prefix}-title`}>
        {content.title}
      </p>
      <p className="text-sm text-gray-500 mt-1.5 max-w-md mx-auto" data-testid={`${prefix}-description`}>
        {content.description}
      </p>
      {stage && (
        <p className="text-xs text-gray-400 mt-3" data-testid={`${prefix}-stage`}>
          Tailored to your stage: {JOURNEY_STAGE_LABELS[stage]}
        </p>
      )}
      <div className="flex flex-wrap gap-2 justify-center mt-4">
        {(content.actions || []).map((action, idx) => {
          // B19 — no shortcut into a section switched off in shared/hidden-sections.ts.
          if (action.href && isHiddenAppPath(action.href)) return null;
          const isPrimary = (action.variant ?? "outline") === "default";
          const handleClick = action.onClick
            ? action.onClick
            : (!action.href && primaryAction ? primaryAction.onClick : undefined);
          const label = action.label;
          const button = (
            <Button
              key={`${prefix}-action-${idx}`}
              size="sm"
              variant={isPrimary ? "default" : "outline"}
              onClick={handleClick}
              data-testid={`${prefix}-action-${idx}`}
            >
              {label}
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          );
          if (action.href) {
            return (
              <Link key={`${prefix}-link-${idx}`} href={action.href}>
                {button}
              </Link>
            );
          }
          return button;
        })}
      </div>
    </div>
  );
}
